import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, History, Loader2, MessageSquarePlus, Send, Sparkles, Square, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Roles } from '@/config/roles';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { MiloAnswer, MiloThinking } from './MiloAnswer';
import { MiloMyNotes } from './MiloMyNotes';
import { useConversations, useDeleteConversation, useMiloChat, useMiloMe } from './hooks';

const STARTERS = ['late', 'today', 'free', 'where'] as const;

/** Damis AI: the round button bottom-right, and the panel it opens (a right-hand panel on desktop, the whole screen on a phone) */
export function MiloPanel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = !!user && (user.isSuperAdmin || user.roles.includes(Roles.ADMIN));
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const me = useMiloMe();
  const chat = useMiloChat();
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [draft, setDraft] = useState('');
  const conversations = useConversations(open && showHistory);
  const remove = useDeleteConversation();
  const bottom = useRef<HTMLDivElement>(null);
  const { state, refusal } = chat;

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [state.messages, state.progress]);

  // Dispatchers see nothing until an admin has set it up; admins see the button to find the settings
  if (!me.data || (!me.data.available && !(isAdmin && me.data.pausedReason === 'unconfigured'))) return null;

  const send = async (text: string) => {
    const question = text.trim();
    if (question === '' || state.busy) return;
    setDraft('');
    setShowHistory(false);
    setShowNotes(false);
    const answered = await chat.ask(question);
    if (!answered) setDraft(question);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void send(draft);
    }
  };

  const onLink = (path: string) => {
    navigate(path);
    if (isMobile) setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('fleet.milo.open')}
        data-qa-id="milo-button"
        className="fixed right-4 bottom-4 z-40 flex h-12 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:scale-105 motion-reduce:transition-none md:right-6 md:bottom-6"
      >
        <Sparkles className="size-4" />
        AI
      </button>
    );
  }

  const remaining = state.remainingToday ?? me.data.remainingToday;
  const longChatNotice = !refusal && state.longChat;
  const notice = refusal
    ? t(`fleet.milo.refusal.${refusal}`)
    : state.longChat
      ? t('fleet.milo.longChat')
      : me.data.available && remaining <= 5
        ? t('fleet.milo.remaining', { count: remaining })
        : null;

  return (
    <aside
      role="dialog"
      aria-label={t('fleet.milo.title')}
      data-qa-id="milo-panel"
      className="fixed inset-0 z-50 flex flex-col bg-card text-card-foreground md:inset-auto md:top-4 md:right-4 md:bottom-4 md:w-[400px] md:rounded-2xl md:border md:shadow-2xl"
    >
      <header className="flex h-14 shrink-0 items-center gap-1 border-b px-3">
        <Sparkles className="size-4 text-primary" />
        <h2 className="mr-auto text-sm font-semibold">{t('fleet.milo.title')}</h2>
        <Button variant="ghost" size="icon-sm" onClick={() => { setShowNotes((value) => !value); setShowHistory(false); }} aria-label={showNotes ? t('fleet.milo.back') : t('fleet.milo.myNotes.title')} data-qa-id="milo-my-notes-button">
          <BookOpen className="size-4" />
        </Button>
        <Button variant="ghost" size="icon-sm" onClick={() => { setShowHistory((value) => !value); setShowNotes(false); }} aria-label={showHistory ? t('fleet.milo.back') : t('fleet.milo.history')} data-qa-id="milo-history-button">
          <History className="size-4" />
        </Button>
        <Button variant="ghost" size="icon-sm" onClick={() => { chat.reset(); setShowHistory(false); setShowNotes(false); }} aria-label={t('fleet.milo.newChat')} data-qa-id="milo-new-button">
          <MessageSquarePlus className="size-4" />
        </Button>
        <Button variant="ghost" size="icon-sm" onClick={() => setOpen(false)} aria-label={t('fleet.milo.close')} data-qa-id="milo-close-button">
          <X className="size-4" />
        </Button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {!me.data.available ? (
          <div className="space-y-2 text-sm" data-qa-id="milo-setup">
            <p>{t('fleet.milo.setup')}</p>
            <Link to="/milo" className="font-medium text-primary hover:underline" onClick={() => isMobile && setOpen(false)}>
              {t('fleet.milo.setupLink')}
            </Link>
          </div>
        ) : showNotes ? (
          <MiloMyNotes />
        ) : showHistory ? (
          <ul className="space-y-1" data-qa-id="milo-history">
            {(conversations.data ?? []).length === 0 && <li className="text-sm text-muted-foreground">{t('fleet.milo.historyEmpty')}</li>}
            {(conversations.data ?? []).map((conversation) => (
              <li key={conversation.id} className="group flex items-center gap-1 rounded-lg hover:bg-accent">
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate px-2 py-2 text-left text-sm"
                  onClick={() => {
                    void chat.open(conversation.id);
                    setShowHistory(false);
                  }}
                  data-qa-id="milo-history-item"
                >
                  {conversation.title}
                </button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                  aria-label={t('fleet.milo.delete')}
                  onClick={() => {
                    remove.mutate(conversation.id);
                    if (conversation.id === state.conversationId) chat.reset();
                  }}
                  data-qa-id="milo-history-delete"
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        ) : state.messages.length === 0 ? (
          <div className="flex h-full flex-col justify-end gap-2" data-qa-id="milo-starters">
            <p className="mb-1 text-xs text-muted-foreground">
              {t('fleet.milo.hint')} {t('fleet.milo.adminsSee')}
            </p>
            {STARTERS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => void send(t(`fleet.milo.starters.${key}`))}
                className="rounded-xl border px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
                data-qa-id={`milo-starter-${key}`}
              >
                {t(`fleet.milo.starters.${key}`)}
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-4" data-qa-id="milo-messages">
            {state.messages.map((message) =>
              message.role === 'user' ? (
                <p key={message.id} className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-md bg-primary px-3 py-2 text-sm whitespace-pre-wrap text-primary-foreground">
                  {message.text}
                </p>
              ) : message.pending && message.text === '' ? (
                <div key={message.id} className="space-y-2">
                  {message.thinking?.trim() && <MiloThinking text={message.thinking} />}
                  <p className="flex items-center gap-2 text-sm text-muted-foreground" data-qa-id="milo-progress">
                    <Loader2 className="size-4 animate-spin motion-reduce:animate-none" />
                    {state.progress ? t(`fleet.milo.progress.${state.progress}`, { defaultValue: t('fleet.milo.progress.default') }) : t('fleet.milo.thinking')}
                  </p>
                </div>
              ) : (
                <MiloAnswer key={message.id} message={message} onLink={onLink} />
              )
            )}
            <div ref={bottom} />
          </div>
        )}
      </div>

      {me.data.available && (
        <footer className="shrink-0 border-t p-3">
          {notice && (
            <p className="mb-2 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground" role="status" data-qa-id="milo-notice">
              {notice}
              {longChatNotice && (
                <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => void chat.continueChat()} disabled={state.busy} data-qa-id="milo-continue-button">
                  {t('fleet.milo.continue')}
                </Button>
              )}
            </p>
          )}
          <div className="flex items-end gap-2">
            <Textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t('fleet.milo.placeholder')}
              rows={1}
              maxLength={2000}
              className="max-h-32 min-h-10 resize-none"
              data-qa-id="milo-input"
            />
            {state.busy ? (
              <Button size="icon" variant="outline" onClick={chat.stop} aria-label={t('fleet.milo.stop')} data-qa-id="milo-stop-button">
                <Square className="size-4" />
              </Button>
            ) : (
              <Button size="icon" onClick={() => void send(draft)} disabled={draft.trim() === ''} aria-label={t('fleet.milo.send')} data-qa-id="milo-send-button">
                <Send className="size-4" />
              </Button>
            )}
          </div>
        </footer>
      )}
    </aside>
  );
}
